import * as React from 'react'

import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Stack,
	TextField,
	Typography,
} from '@mui/material'
import { alpha, type Theme } from '@mui/material/styles'

import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import CloseIcon from '@mui/icons-material/Close'
import TagOutlinedIcon from '@mui/icons-material/TagOutlined'

import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs, { type Dayjs } from 'dayjs'

import { BillingLogSelect } from './BillingLogSelect'
import { getBillingLogEntries } from '../../services/billingLogService'
import type { BillingLogEntryResponse } from '../../types/Contracts/BillingLog'
import type { UpdateWorkActivityRequest } from '../../types/Work/WorkEntry'

import { formatLocaleShortDate } from '../../services/CommonServices/DataFormatters'

/** Subtle lift on outlined inputs so fields read as cards. */
function outlinedTextFieldShadowSx(theme: Theme) {
	return {
		'& .MuiOutlinedInput-root': {
			boxShadow:
				theme.palette.mode === 'dark'
					? `0 1px 4px ${alpha(theme.palette.common.black, 0.45)}`
					: `0 1px 6px ${alpha(theme.palette.common.black, 0.1)}`,
		},
	}
}

type RowEditDraft = {
	wo: string
	billingLogEntryId: number | null
	requestDate: string
	plannedDate: string
	plannedHrs: string
	percentComplete: string
	details: string
}

function dateToInputValue(d: string | null): string {
	if (!d) return ''
	const iso = /^(\d{4}-\d{2}-\d{2})/.exec(d.trim())
	if (iso) return iso[1]
	const parsed = new Date(d)
	if (Number.isNaN(parsed.getTime())) return ''
	const y = parsed.getFullYear()
	const mo = String(parsed.getMonth() + 1).padStart(2, '0')
	const da = String(parsed.getDate()).padStart(2, '0')
	return `${y}-${mo}-${da}`
}

function parseDraftDate(iso: string): Dayjs | null {
	if (!iso.trim()) return null
	const d = dayjs(iso)
	return d.isValid() ? d : null
}

function draftFromRecord(r: UpdateWorkActivityRequest): RowEditDraft {
	return {
		wo: r.wo?.trim() ?? '',
		billingLogEntryId: r.billingLogEntryId,
		requestDate: dateToInputValue(r.requestDate),
		plannedDate: dateToInputValue(r.plannedDate),
		plannedHrs: r.plannedHrs != null && Number.isFinite(r.plannedHrs) ? String(r.plannedHrs) : '',
		percentComplete:
			r.percentComplete != null && Number.isFinite(r.percentComplete)
				? String(r.percentComplete)
				: '',
		details: r.details ?? '',
	}
}

type ReadOnlyStatProps = {
	icon: React.ReactNode
	label: string
	value: string
}

function ReadOnlyStat({ icon, label, value }: ReadOnlyStatProps) {
	return (
		<Stack direction='row' spacing={1.25} alignItems='flex-start' sx={{ minWidth: 0, flex: 1 }}>
			<Box
				aria-hidden
				sx={(theme) => ({
					flexShrink: 0,
					width: 40,
					height: 40,
					borderRadius: 1,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					color: 'primary.main',
					bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.2 : 0.12),
				})}
			>
				{icon}
			</Box>
			<Stack spacing={0.25} sx={{ minWidth: 0, pt: 0.125 }}>
				<Typography
					variant='caption'
					color='text.secondary'
					fontWeight={600}
					letterSpacing={0.04}
					sx={{ textTransform: 'uppercase', lineHeight: 1.2 }}
				>
					{label}
				</Typography>
				<Typography
					variant='body1'
					fontWeight={600}
					color='text.primary'
					sx={{ lineHeight: 1.35, wordBreak: 'break-word' }}
				>
					{value}
				</Typography>
			</Stack>
		</Stack>
	)
}

export type WarActivityEditDialogProps = {
	open: boolean
	record: UpdateWorkActivityRequest | null
	saving?: boolean
	onClose: () => void
	onSave: (updated: UpdateWorkActivityRequest) => void
}

export function WarActivityEditDialog({
	open,
	record,
	saving = false,
	onClose,
	onSave,
}: WarActivityEditDialogProps) {
	const [editDraft, setEditDraft] = React.useState<RowEditDraft | null>(null)
	const [billingLogEntries, setBillingLogEntries] = React.useState<BillingLogEntryResponse[]>([])
	const [billingLogEntriesLoading, setBillingLogEntriesLoading] = React.useState(false)

	React.useEffect(() => {
		if (record) {
			setEditDraft(draftFromRecord(record))
		} else {
			setEditDraft(null)
		}
	}, [record])

	React.useEffect(() => {
		if (!open) return
		let cancelled = false
		const loadBillingLogEntries = async () => {
			setBillingLogEntriesLoading(true)
			try {
				const logs = await getBillingLogEntries()
				if (!cancelled) setBillingLogEntries(logs)
			} catch {
				if (!cancelled) setBillingLogEntries([])
			} finally {
				if (!cancelled) setBillingLogEntriesLoading(false)
			}
		}
		void loadBillingLogEntries()
		return () => {
			cancelled = true
		}
	}, [open])

	const handleSave = () => {
		if (!record || !editDraft) return

		const plannedHrs = editDraft.plannedHrs.trim() === '' ? null : Number(editDraft.plannedHrs)

		const percentComplete =
			editDraft.percentComplete.trim() === '' ? null : Number(editDraft.percentComplete)

		onSave({
			...record,
			requestDate: editDraft.requestDate.trim() ? editDraft.requestDate : null,
			plannedDate: editDraft.plannedDate.trim() ? editDraft.plannedDate : null,
			plannedHrs: plannedHrs !== null && Number.isFinite(plannedHrs) ? plannedHrs : null,
			percentComplete:
				percentComplete !== null && Number.isFinite(percentComplete) ? percentComplete : null,
			details: editDraft.details,
			wo: editDraft.wo.trim() !== '' ? editDraft.wo.trim() : null,
			billingLogEntryId: editDraft.billingLogEntryId,
		})
	}

	return (
		<Dialog
			open={open}
			onClose={(_e, _reason) => {
				if (saving) return
				onClose()
			}}
			maxWidth='sm'
			fullWidth
		>
			<DialogTitle sx={{ pr: 6, pb: 2 }}>
				<Stack spacing={1.5}>
					<Typography variant='h6' component='h2' fontWeight={700} letterSpacing={-0.02}>
						Edit activity record
					</Typography>

					{record ? (
						<Box
							sx={(theme) => ({
								borderRadius: 2,
								border: `1px solid ${theme.palette.divider}`,
								bgcolor:
									theme.palette.mode === 'dark'
										? alpha(theme.palette.common.white, 0.04)
										: alpha(theme.palette.primary.main, 0.04),
								boxShadow:
									theme.palette.mode === 'dark'
										? 'none'
										: `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`,
								px: { xs: 1.5, sm: 2 },
								py: 1.75,
							})}
						>
							<Stack
								direction={{ xs: 'column', sm: 'row' }}
								spacing={{ xs: 2, sm: 0 }}
								sx={{ alignItems: { sm: 'stretch' } }}
							>
								<ReadOnlyStat
									icon={<CategoryOutlinedIcon sx={{ fontSize: 22 }} />}
									label='Activity type'
									value={record.activityType || '—'}
								/>
								<ReadOnlyStat
									icon={<TagOutlinedIcon sx={{ fontSize: 22 }} />}
									label='WAR ID'
									value={record.warIdSummary || '—'}
								/>
								<ReadOnlyStat
									icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 22 }} />}
									label='Created'
									value={formatLocaleShortDate(record.createdAt, '—', 'en-GB')}
								/>
							</Stack>
						</Box>
					) : null}
				</Stack>
				<IconButton
					aria-label='close'
					onClick={onClose}
					disabled={saving}
					sx={(theme) => ({
						position: 'absolute',
						right: 8,
						top: 8,
						color: theme.palette.grey[500],
					})}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers>
				{record && editDraft ? (
					<Stack spacing={2} sx={{ pt: 2}}>
						<TextField
							label='Work Order'
							size='medium'
							value={editDraft.wo}
							onChange={(e) =>
								setEditDraft((prev) => (prev ? { ...prev, wo: e.target.value } : prev))
							}
							inputProps={{ maxLength: 500 }}
							fullWidth
							sx={outlinedTextFieldShadowSx}
						/>
						<BillingLogSelect
							value={editDraft.billingLogEntryId}
							onChange={(billingLogEntryId) =>
								setEditDraft((prev) => (prev ? { ...prev, billingLogEntryId } : prev))
							}
							logs={billingLogEntries}
							loading={billingLogEntriesLoading}
						/>
						<DatePicker
							label='Request Date'
							inputFormat='DD/MM/YYYY'
							value={parseDraftDate(editDraft.requestDate)}
							onChange={(value: Dayjs | null) =>
								setEditDraft((prev) =>
									prev
										? {
												...prev,
												requestDate: value && value.isValid() ? value.format('YYYY-MM-DD') : '',
										  }
										: prev,
								)
							}
							renderInput={(params) => (
								<TextField {...params} size='medium' fullWidth sx={outlinedTextFieldShadowSx} />
							)}
						/>
						<DatePicker
							label='Planned Date'
							inputFormat='DD/MM/YYYY'
							value={parseDraftDate(editDraft.plannedDate)}
							onChange={(value: Dayjs | null) =>
								setEditDraft((prev) =>
									prev
										? {
												...prev,
												plannedDate: value && value.isValid() ? value.format('YYYY-MM-DD') : '',
										  }
										: prev,
								)
							}
							renderInput={(params) => (
								<TextField {...params} size='medium' fullWidth sx={outlinedTextFieldShadowSx} />
							)}
						/>
						<Stack direction='row' spacing={2}>
							<TextField
								label='Planned Hrs'
								size='medium'
								value={editDraft.plannedHrs}
								onChange={(e) =>
									setEditDraft((prev) => (prev ? { ...prev, plannedHrs: e.target.value } : prev))
								}
								fullWidth
								sx={(theme) => ({
									...outlinedTextFieldShadowSx(theme),
									backgroundColor:
										editDraft.plannedHrs &&
										editDraft.plannedHrs.trim() !== '' &&
										Number(editDraft.plannedHrs) < 0
											? 'rgba(255,230,222,1)'
											: 'inherit',
								})}
							/>
							<TextField
								label='% Comp'
								type='number'
								size='medium'
								value={editDraft.percentComplete}
								onChange={(e) =>
									setEditDraft((prev) =>
										prev ? { ...prev, percentComplete: e.target.value } : prev,
									)
								}
								fullWidth
								inputProps={{ min: 0, max: 100, step: 'any' }}
								sx={(theme) => ({
									...outlinedTextFieldShadowSx(theme),
									backgroundColor:
										editDraft.percentComplete &&
										editDraft.percentComplete.trim() !== '' &&
										(Number(editDraft.percentComplete) < 0 ||
											Number(editDraft.percentComplete) > 100)
											? 'rgba(255,230,222,1)'
											: 'inherit',
								})}
							/>
						</Stack>
						<TextField
							label='Details'
							size='medium'
							value={editDraft.details}
							onChange={(e) =>
								setEditDraft((prev) => (prev ? { ...prev, details: e.target.value } : prev))
							}
							fullWidth
							multiline
							minRows={3}
							sx={outlinedTextFieldShadowSx}
						/>
					</Stack>
				) : null}
			</DialogContent>

			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button onClick={onClose} color='inherit' sx={{ color: 'text.primary' }} disabled={saving}>
					Cancel
				</Button>

				<Button
					onClick={handleSave}
					variant='contained'
					color='primary'
					disabled={!record || !editDraft || saving}
				>
					{saving ? 'Saving…' : 'Save'}
				</Button>
			</DialogActions>
		</Dialog>
	)
}
